<script setup lang="ts">
import { withLeadingSlash } from 'ufo';
import type { Collections } from '@nuxt/content';
import { createLogger } from '#bubba/shared/utils/logger';
import { useSchemaOrg, defineWebPage, defineArticle, defineBreadcrumb, useSeoMeta, useHead, useI18n, useRoute, useAsyncData, queryCollection, createError, navigateTo, provide, watch, computed, useRuntimeConfig, definePageMeta } from '#imports';

const log = createLogger('ContentPage');

const route = useRoute();
const { $getLocale, $localePath, $ts } = useI18n();
const locale = computed(() => $getLocale(route));

definePageMeta({
  layout: 'content',
});

// Process the slug correctly for arrays
const slug = computed(() => {
  const params = route.params.slug;

  // Handle array of slug segments (from [...slug])
  if (Array.isArray(params)) {
    return withLeadingSlash(params.join('/'));
  }

  // Handle single string
  return withLeadingSlash(String(params));
});

// Function to load content
const loadContent = async (path: string) => {
  try {
    // Remove locale from path if it exists
    const pathWithoutLocale = path.replace(`/${locale.value}`, '');

    // Load content, removing initial "/" for correct path in queryContent
    const docPath = pathWithoutLocale.startsWith('/')
      ? pathWithoutLocale.slice(1)
      : pathWithoutLocale;

    const collection = ('content_' + locale.value) as keyof Collections;

    // For non-English locales, the content.config.ts adds a prefix to paths
    // So we need to query with the locale prefix for non-English content
    const queryPath =
      locale.value === 'en' ? `/${docPath}` : `/${locale.value}/${docPath}`;

    let content = await queryCollection(collection).path(queryPath).first();

    // Fallback to default locale if content is missing
    if (!content && locale.value !== 'en') {
      content = await queryCollection('content_en').path(`/${docPath}`).first();
    }

    return content;
  } catch (error) {
    log.error('Failed to load content:', error);
    return null;
  }
};

const { data: page, error: pageError } = await useAsyncData(
  () => `page-${locale.value}-${slug.value}`,
  async () => {
    const content = await loadContent(route.path);
    if (!content) {
      log.warn('Page not found - showing 404:', {
        requestedPath: route.path,
        slug: slug.value,
        locale: locale.value,
        userAgent: import.meta.client ? navigator.userAgent : 'server-side',
        timestamp: new Date().toISOString(),
      });
      throw createError({ status: 404, statusText: 'Page not found' });
    }
    return content;
  },
  {
    watch: [locale],
  },
);

if (pageError.value) {
  throw createError(pageError.value);
}

watch(
  () => route.path,
  async (newPath, oldPath) => {
    if (!oldPath) {
      return;
    }

    if (newPath) {
      const newContent = await loadContent(newPath);
      if (newContent) {
        // Update page.value directly
        page.value = newContent;
      } else {
        // Navigate to 404 page, replacing current entry to prevent back button issues
        log.warn('Content not found during route change:', {
          newPath,
          timestamp: new Date().toISOString(),
        });
        await navigateTo($localePath('/404'), { replace: true });
      }
    }
  },
);

const { public: { siteUrl } } = useRuntimeConfig();

const fullUrl = computed(() => {
  return `${siteUrl}${route.path}`;
});

// Helper to resolve absolute image URLs
const absoluteImageUrl = computed(() => {
  if (!page.value?.image) return undefined;
  const imagePath = typeof page.value.image === 'string'
    ? page.value.image
    : page.value.image.src;

  return imagePath.startsWith('http')
    ? imagePath
    : `${siteUrl}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
});

// Generate SEO meta tags from content frontmatter
const seoMeta = computed(() => {
  if (!page.value) return {};

  const title = page.value.title || 'Bubba';
  const description = page.value.description || '';
  const isArticle = page.value.articleType === 'BlogPosting';

  // Build SEO object from frontmatter
  const meta: Record<string, unknown> = {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogUrl: fullUrl.value,
    ogType: isArticle ? 'article' : 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };

  // Add absolute image URL if available
  if (absoluteImageUrl.value) {
    meta.ogImage = absoluteImageUrl.value;
    meta.twitterImage = absoluteImageUrl.value;
  }

  // Add article-specific meta for blog posts
  if (isArticle) {
    if (page.value.publishedDate) {
      meta.articlePublishedTime = page.value.publishedDate;
    }
    if (page.value.modifiedDate) {
      meta.articleModifiedTime = page.value.modifiedDate;
    }
    if (page.value.author) {
      meta.articleAuthor = page.value.author;
    }
    if (page.value.category) {
      meta.articleSection = page.value.category;
    }
    if (Array.isArray(page.value.tags) && page.value.tags.length > 0) {
      meta.articleTag = page.value.tags;
    }
  }

  return meta;
});

// Apply SEO meta tags
// @ts-expect-error — seoMeta is dynamically built from content frontmatter
useSeoMeta(() => seoMeta.value);

// Render head data including canonical URL
useHead(() => {
  const head = page.value?.head;
  return {
    ...(head?.meta ? { meta: head.meta as Record<string, string>[] } : {}),
    ...(head?.script ? { script: head.script as Record<string, string>[] } : {}),
    link: [
      { rel: 'canonical', href: fullUrl.value },
    ],
  };
});

// Breadcrumb structured data
const breadcrumbs = computed(() => {
  const items = [{ name: $ts('nav.home'), item: $localePath('/') }];

  // Extract segments from the slug (e.g. /en/blog/post -> ['blog', 'post'])
  const segments = route.path
    .replace(`/${locale.value}`, '')
    .split('/')
    .filter(Boolean);

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    // Last one will be the page title, others are capitalized segment names
    let name = segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    if (isLast) {
      name = page.value?.title || name;
    }

    items.push({
      name,
      item: isLast ? '' : $localePath(currentPath),
    });
  });

  return items;
});

// Structured data via nuxt-schema-org
useSchemaOrg(() => {
  if (!page.value) return [];

  const nodes = [];

  // 1. Breadcrumbs
  nodes.push(defineBreadcrumb({
    itemListElement: breadcrumbs.value,
  }));

  // 2. Main content schema (Article or WebPage)
  const isArticle = page.value.articleType === 'BlogPosting';

  if (isArticle) {
    nodes.push(defineArticle({
      '@type': 'BlogPosting',
      headline: page.value.title,
      description: page.value.description,
      ...(absoluteImageUrl.value ? { image: absoluteImageUrl.value } : {}),
      ...(page.value.publishedDate ? { datePublished: new Date(page.value.publishedDate).toISOString() } : {}),
      ...(page.value.modifiedDate ? { dateModified: new Date(page.value.modifiedDate).toISOString() } : {}),
      ...(page.value.author ? {
        author: {
          name: page.value.author,
          ...(page.value.authorUrl ? { url: page.value.authorUrl } : {}),
        },
      } : {}),
    }));
  } else {
    nodes.push(defineWebPage({
      name: page.value.title,
      description: page.value.description,
    }));
  }

  return nodes;
});

// Provide page data to child components
provide('pageData', page);
</script>

<template>
  <article class="prose prose-lg dark:prose-invert max-w-none [&_hr]:my-10 [&_hr]:border-border">
    <ContentRenderer v-if="page" :value="page"/>
    <div v-else class="text-center py-12 text-muted-foreground">{{ $ts('common.loading') }}</div>
  </article>
</template>
